import React from 'react';

import type {
  CardProps,
  CardHeaderProps,
  CardBodyProps,
  CardFooterProps,
  CardPadding,
  CardStroke,
} from './Card.types';

/**
 * Padding class map for card components
 */
const PADDING_CLASSES: Record<CardPadding, string> = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

/**
 * Border class map. `subtle` is the default card; `default` is the documented
 * dashboard-module exception (colour-system.md, strokes table).
 */
const STROKE_CLASSES: Record<CardStroke, string> = {
  subtle: 'border-stroke-subtle',
  default: 'border-stroke-default',
};

/**
 * Card component - base container for card-based layouts
 *
 * Features:
 * - Configurable padding sizes (none, sm, md, lg)
 * - Rounded corners (rounded-xl)
 * - Hairline alpha-white border (`stroke`: subtle by default)
 *
 * A card carries NO shadow. Per docs/guides/colour-system.md the page is one
 * tone and depth comes from light, not paint: a card is lifted by its
 * alpha-white fill (`contrast-700`) plus a `stroke-subtle` hairline. Shadows are
 * reserved for overlays that genuinely float above the page — popovers, sheets
 * and modals, via the `lift-*` shadow tokens. Do not reintroduce `shadow-sm`
 * here.
 *
 * @example
 * ```tsx
 * <Card>
 *   <CardHeader title="Card Title" />
 *   <CardBody>Content here</CardBody>
 *   <CardFooter>
 *     <button>Action</button>
 *   </CardFooter>
 * </Card>
 * ```
 */
export function Card({
  children,
  padding = 'none',
  stroke = 'subtle',
  className = '',
  as: Component = 'div',
  ...props
}: CardProps): React.JSX.Element {
  const paddingClasses = PADDING_CLASSES[padding];

  return (
    <Component
      className={`card bg-contrast-700 rounded-xl border ${STROKE_CLASSES[stroke]} ${paddingClasses} ${className}`.trim()}
      {...props}
    >
      {children}
    </Component>
  );
}

/**
 * CardHeader component - header section with title and optional actions
 *
 * @example
 * ```tsx
 * <CardHeader
 *   title="My Card"
 *   actions={<button>Menu</button>}
 * />
 * ```
 */
export function CardHeader({
  title,
  children,
  actions,
  className = '',
  ...props
}: CardHeaderProps): React.JSX.Element {
  return (
    <div
      className={`card-header flex items-center justify-between px-6 py-4 border-b border-stroke-subtle ${className}`.trim()}
      {...props}
    >
      <div className="flex-1 min-w-0">
        {title ? (
          <h3 className="font-display text-heading-sm font-semibold text-neutral-50 truncate">
            {title}
          </h3>
        ) : (
          children
        )}
      </div>
      {actions && (
        <div className="flex items-center gap-2 ml-4 shrink-0">{actions}</div>
      )}
    </div>
  );
}

/**
 * CardBody component - main content area of the card
 *
 * @example
 * ```tsx
 * <CardBody padding="md">
 *   <p>Card content goes here</p>
 * </CardBody>
 * ```
 */
export function CardBody({
  children,
  padding = 'md',
  className = '',
  ...props
}: CardBodyProps): React.JSX.Element {
  const paddingClasses = PADDING_CLASSES[padding];

  return (
    <div
      className={`card-body ${paddingClasses} ${className}`.trim()}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * Footer alignment class map
 */
const FOOTER_ALIGN_CLASSES = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
  between: 'justify-between',
};

/**
 * CardFooter component - footer section for actions/buttons
 *
 * @example
 * ```tsx
 * <CardFooter align="right">
 *   <button>Cancel</button>
 *   <button>Save</button>
 * </CardFooter>
 * ```
 */
export function CardFooter({
  children,
  align = 'right',
  className = '',
  ...props
}: CardFooterProps): React.JSX.Element {
  const alignClasses = FOOTER_ALIGN_CLASSES[align];

  return (
    <div
      className={`card-footer flex items-center gap-3 px-6 py-4 border-t border-stroke-subtle ${alignClasses} ${className}`.trim()}
      {...props}
    >
      {children}
    </div>
  );
}

export default Card;
